import { Pipe, PipeTransform } from "@angular/core";

/**
 * Filtro para ordenar valores alfanuméricos considerando a parte numérica. *
 */
@Pipe({
    name: "alphanum"
})
export class AlphanumPipe implements PipeTransform {

    /**
     * Método principal que transforma e compara duas strings alfanuméricas.
     */
    transform(a: string, b: string, caseInsensitive = false): number {
        a = this.normalizeInput(a);
        b = this.normalizeInput(b);
        if (caseInsensitive) {
            a = a.toLowerCase();
            b = b.toLowerCase();
        }
        const specialComparisonResult = this.handleSpecialCases(a, b);
        if (specialComparisonResult !== null) {
            return specialComparisonResult;
        }

        return this.compareAlphaNum(a, b);
    }

    /**
     * Normaliza a entrada para garantir que seja uma string válida.
     */
    private normalizeInput(input: any): string {
        if (input === null || input === undefined) {
            return "";
        }
        return typeof input === "string" ? input : String(input);
    }

    /**
     * Verifica e compara casos especiais como datas, versões e números decimais.
     */
    private handleSpecialCases(a: string, b: string): number | null {
        const dateComparisonResult = this.compareDates(a, b);
        if (dateComparisonResult !== null) {
            return dateComparisonResult;
        }

        const versionComparisonResult = this.compareVersions(a, b);
        if (versionComparisonResult !== null) {
            return versionComparisonResult;
        }

        const floatComparisonResult = this.compareFloats(a, b);
        if (floatComparisonResult !== null) {
            return floatComparisonResult;
        }

        return null;
    }

    /**
     * Compara duas strings que representam datas no formato XX/XX/XX.
     */
    private compareDates(a: string, b: string): number | null {
        const datePattern = /\d{1,4}\/\d{1,2}\/\d{1,4}/;
        if (!this.containsPattern(a, datePattern) ||
            !this.containsPattern(b, datePattern)) {
            return null;
        }

        const prefixA = a.substring(0, a.indexOf("/")).trim();
        const prefixB = b.substring(0, b.indexOf("/")).trim();

        if (prefixA !== prefixB) {
            return null;
        }

        const match1 = a.match(/(\d{1,4})\/(\d{1,2})\/(\d{1,4})/);
        const match2 = b.match(/(\d{1,4})\/(\d{1,2})\/(\d{1,4})/);

        if (!match1 || !match2) {
            return null;
        }

        for (let i = 1; i <= 3; i++) {
            const numA = parseInt(match1[i], 10);
            const numB = parseInt(match2[i], 10);
            if (numA !== numB) {
                return numA - numB;
            }
        }

        return 0;
    }

    /**
     * Compara duas strings que representam versões (ex: v1.2.3).
     */
    private compareVersions(a: string, b: string): number | null {
        const versionPattern = /^v?\d{1,10}(\.\d{1,10}){0,9}$/;
        if (!this.containsPattern(a, versionPattern) ||
            !this.containsPattern(b, versionPattern)) {
            return null;
        }

        const nums1 = a.replace(/^v/, "").split(".").map(n => parseInt(n, 10));
        const nums2 = b.replace(/^v/, "").split(".").map(n => parseInt(n, 10));

        return this.compareNumericArrays(nums1, nums2);
    }

    /**
     * Compara dois arrays numéricos elemento por elemento.
     */
    private compareNumericArrays(arr1: number[], arr2: number[]): number {
        for (let i = 0; i < Math.max(arr1.length, arr2.length); i++) {
            const n1 = i < arr1.length ? arr1[i] : 0;
            const n2 = i < arr2.length ? arr2[i] : 0;
            if (n1 !== n2) {
                return n1 < n2 ? -1 : 1;
            }
        }
        return 0;
    }

    /**
     * Compara duas strings que representam números decimais.
     */
    private compareFloats(a: string, b: string): number | null {
        const floatPattern = /^\d{1,20}\.\d{1,20}$/;
        if (!this.containsPattern(a, floatPattern) ||
            !this.containsPattern(b, floatPattern)) {
            return null;
        }

        const numA = parseFloat(a);
        const numB = parseFloat(b);

        return this.compareNumbers(numA, numB);
    }

    /**
     * Compara dois números.
     */
    private compareNumbers(a: number, b: number): number {
        if (a < b) {
            return -1;
        }
        if (a > b) {
            return 1;
        }
        return 0;
    }

    /**
     * Verifica se uma string corresponde a um padrão regular.
     */
    private containsPattern(str: string, pattern: RegExp): boolean {
        return pattern.test(str);
    }

    /**
     * Compara duas strings alfanuméricas dividindo-as em chunks.
     */
    private compareAlphaNum(a: string, b: string): number {
        const chunksA = this.tokenize(a);
        const chunksB = this.tokenize(b);

        const comparisonResult = this.compareChunks(chunksA, chunksB);
        if (comparisonResult !== 0) {
            return comparisonResult;
        }

        return this.compareArrayLengths(chunksA.length, chunksB.length);
    }

    /**
     * Compara os chunks comuns entre dois arrays de chunks alfanuméricos.
     */
    private compareChunks(
        chunksA: Array<{value: string; type: string; numValue: number}>,
        chunksB: Array<{value: string; type: string; numValue: number}>
    ): number {
        const minLength = Math.min(chunksA.length, chunksB.length);

        for (let i = 0; i < minLength; i++) {
            const chunkA = chunksA[i];
            const chunkB = chunksB[i];

            if (chunkA.value === chunkB.value) {
                continue;
            }

            return this.compareChunkValues(chunkA, chunkB);
        }

        return 0;
    }

    /**
     * Compara dois chunks individuais considerando seus tipos e valores.
     */
    private compareChunkValues(
        chunkA: {value: string; type: string; numValue: number},
        chunkB: {value: string; type: string; numValue: number}
    ): number {
        if (chunkA.type !== chunkB.type) {
            return chunkA.type === "number" ? -1 : 1;
        }

        if (chunkA.type === "number") {
            return chunkA.numValue < chunkB.numValue ? -1 : 1;
        }

        return chunkA.value < chunkB.value ? -1 : 1;
    }

    /**
     * Compara os comprimentos de dois arrays.
     */
    private compareArrayLengths(lengthA: number, lengthB: number): number {
        if (lengthA === lengthB) {
            return 0;
        }
        return lengthA < lengthB ? -1 : 1;
    }

    /**
     * Divide uma string em chunks alfanuméricos, identificando números e texto.
     */
    private tokenize(text: string): Array<{value: string; type: string; numValue: number}> {
        const safeText = text.length > 5000 ? text.substring(0, 5000) : text;

        const chunks: string[] = [];
        let currentIndex = 0;

        while (currentIndex < safeText.length) {
            const remainingText = safeText.substring(currentIndex);

            const decimalMatch = /^\d{1,20}\.\d{1,20}/.exec(remainingText);
            if (decimalMatch) {
                chunks.push(decimalMatch[0]);
                currentIndex += decimalMatch[0].length;
                continue;
            }

            const intMatch = /^\d{1,20}/.exec(remainingText);
            if (intMatch) {
                chunks.push(intMatch[0]);
                currentIndex += intMatch[0].length;
                continue;
            }

            chunks.push(remainingText[0]);
            currentIndex++;
        }

        return chunks.map(chunk => {
            const isNumeric = /^\d{1,20}$/.test(chunk) || /^\d{1,20}\.\d{1,20}$/.test(chunk);
            return {
                value: chunk,
                type: isNumeric ? "number" : "text",
                numValue: isNumeric ? parseFloat(chunk) : NaN
            };
        });
    }
}
